// statistics_clinic_07_practice_pvalues_confidence_intervals.md — 3 DIY
// cases requiring both a p-value verdict and a 95% CI, not just a bare
// significant/not-significant call. One is not significant (CI crosses
// zero), one is strongly significant, one is borderline-but-significant.

export const STATS_CLINIC_07_TRACKER_ID = 'statistics-clinic-07-practice-bank';

export const STATS_CLINIC_07_LENSES = [
  ['business_question', 'Business question'],
  ['statistic_family', 'Statistic family'],
  ['statistic_chosen', 'Statistic chosen'],
  ['why_statistic', 'Why this statistic'],
  ['calculation', 'Calculation'],
  ['result', 'Result'],
  ['meaning', 'Meaning'],
  ['decision', 'Decision'],
  ['caution', 'Caution'],
];

export const STATS_CLINIC_07_ITEMS = [
  {
    id: 'DIY01',
    title: 'DIY Practice 01: Support response time initiative',
    scenario: 'A customer support director rolled out a new ticket-routing process to a test group of agents and wants a precise, formal read — not just a rule of thumb — on whether the observed speed-up is real, and how large it might genuinely be, before asking finance to fund a full rollout.',
    tables: [{
      columns: ['group', 'mean_response_minutes', 'sd_minutes', 'n_tickets'],
      rows: [['Control (A, old routing)', '12.0', '3.5', '35'], ['Variant (B, new routing)', '10.8', '3.2', '35']],
    }],
    answerGuide: {
      business_question: 'Is the new ticket-routing process a statistically significant improvement, and how large is the effect?',
      statistic_family: 'Statistical inference.',
      statistic_chosen: 'P-value and 95% confidence interval.',
      why_statistic: 'A significance verdict alone would hide how large (or small) the true speed-up plausibly is.',
      calculation: 'Difference = 10.8-12.0 = -1.2. SE = sqrt(3.5^2/35 + 3.2^2/35) = sqrt(0.35+0.2926) = sqrt(0.6426) = 0.80. t = -1.2/0.80 = -1.50. |t| = 1.5 -> p is well above 0.05, not significant. 95% CI = -1.2 +/- (2x0.80) = (-2.80, 0.40).',
      result: 'Not statistically significant (p > 0.05); the 95% CI (-2.80 to 0.40 minutes) crosses zero.',
      meaning: 'Zero difference is a plausible value given this data — the apparent 1.2-minute speed-up could be chance.',
      decision: 'Do not ask finance to fund a full rollout on this evidence alone — extend the test to a larger sample before deciding.',
      caution: 'The interval crossing zero does not prove there is no effect, only that this sample size could not confirm one with confidence.',
    },
    ruleTerms: {
      business_question: ['significant', 'improvement', 'large'],
      statistic_family: ['statistical inference'],
      statistic_chosen: ['p-value', 'confidence interval'],
      why_statistic: ['verdict alone', 'hide'],
      calculation: ['0.80', '-1.50', '1.5'],
      result: ['not', 'significant', 'crosses zero'],
      meaning: ['plausible', 'chance'],
      decision: ['do not', 'extend the test'],
      caution: ['does not prove', 'no effect'],
    },
    criticalTerms: { result: ['crosses zero'] },
  },
  {
    id: 'DIY02',
    title: 'DIY Practice 02: Pricing page average order value',
    scenario: 'A revenue analyst tested a redesigned pricing page against the current one and wants a precise, board-ready statement of both whether the increase in average order value is real and how large it is likely to be.',
    tables: [{
      columns: ['group', 'mean_order_value_gbp', 'sd_gbp', 'n_orders'],
      rows: [['Control (A)', '85', '18', '55'], ['Variant (B)', '97', '19', '55']],
    }],
    answerGuide: {
      business_question: 'Is the increase in average order value statistically significant, and how large is it likely to be?',
      statistic_family: 'Statistical inference.',
      statistic_chosen: 'P-value and 95% confidence interval.',
      why_statistic: 'A significance verdict alone would not give the board a usable range for the likely revenue impact.',
      calculation: 'Difference = 97-85 = 12. SE = sqrt(18^2/55 + 19^2/55) = sqrt(5.891+6.564) = sqrt(12.455) = 3.53. t = 12/3.53 = 3.40. |t| = 3.40 -> approx p < 0.003. 95% CI = 12 +/- (2x3.53) = (4.94, 19.06).',
      result: 'Statistically significant (p < 0.05, in fact p < 0.003); the 95% CI (£4.94 to £19.06) does not cross zero.',
      meaning: 'We can be confident average order value genuinely increased, somewhere between about £5 and £19.',
      decision: 'Roll out the redesigned pricing page, and use the conservative lower bound (£4.94) when forecasting the revenue impact for the board.',
      caution: 'The interval is fairly wide (£4.94 to £19.06) — a larger sample would narrow it before committing to a precise revenue forecast.',
    },
    ruleTerms: {
      business_question: ['significant', 'order value'],
      statistic_family: ['statistical inference'],
      statistic_chosen: ['p-value', 'confidence interval'],
      why_statistic: ['range', 'revenue impact'],
      calculation: ['3.53', '3.40'],
      result: ['significant', '4.94', '19.06'],
      meaning: ['increased', 'confident'],
      decision: ['roll out', 'lower bound'],
      caution: ['wide', 'larger sample'],
    },
    criticalTerms: { calculation: ['3.40'], result: ['significant'] },
  },
  {
    id: 'DIY03',
    title: 'DIY Practice 03: In-app notification retention test',
    scenario: 'A growth team tested a new in-app notification strategy against no notifications, tracking 7-day retention. Before expanding the notification strategy to the full user base, they want a formal read on both significance and the likely size of the retention lift.',
    tables: [{
      columns: ['group', 'mean_retention_pct', 'sd_pct', 'n_users'],
      rows: [['Control (A, no notifications)', '22.0', '6.0', '50'], ['Variant (B, new notifications)', '25.0', '6.2', '50']],
    }],
    answerGuide: {
      business_question: 'Is the retention lift from the new notification strategy statistically significant, and how large is it?',
      statistic_family: 'Statistical inference.',
      statistic_chosen: 'P-value and 95% confidence interval.',
      why_statistic: 'A significance verdict alone would not show how large the plausible retention lift really is before a full rollout.',
      calculation: 'Difference = 25.0-22.0 = 3.0. SE = sqrt(6.0^2/50 + 6.2^2/50) = sqrt(0.72+0.7688) = sqrt(1.4888) = 1.22. t = 3.0/1.22 = 2.46. |t| = 2.46 -> approx p is around 0.01-0.02, under the 0.05 threshold. 95% CI = 3.0 +/- (2x1.22) = (0.56, 5.44).',
      result: 'Statistically significant (p < 0.05); the 95% CI (0.56 to 5.44 percentage points) does not cross zero, though it is a fairly wide range.',
      meaning: 'The retention lift looks genuine, but the plausible size ranges from barely noticeable (0.56 points) to substantial (5.44 points).',
      decision: 'Expand the notification strategy, but treat the exact retention impact as uncertain until a larger test narrows the interval.',
      caution: 'This result is significant but not as strongly as the pricing-page case (|t| = 2.46 vs 3.40) — treat the effect size estimate with more caution.',
    },
    ruleTerms: {
      business_question: ['significant', 'retention'],
      statistic_family: ['statistical inference'],
      statistic_chosen: ['p-value', 'confidence interval'],
      why_statistic: ['plausible', 'rollout'],
      calculation: ['1.22', '2.46'],
      result: ['significant', '0.56', '5.44'],
      meaning: ['genuine', 'wide'],
      decision: ['expand', 'uncertain'],
      caution: ['not as strongly', 'caution'],
    },
    criticalTerms: { calculation: ['2.46'], result: ['significant'] },
  },
];

export const STATS_CLINIC_07_CONFIG = {
  trackerId: STATS_CLINIC_07_TRACKER_ID,
  resourceKey: 'statistics_clinic_07_practice_pvalues_confidence_intervals',
  rubricKey: 'statistics_pvalues_confidence_intervals',
  activityScope: 'daily_concept_clinic_take_home',
  heading: 'Learner Practice: Savable P-value & Confidence Interval Cases',
  description: "Each case gives two test groups. Compute the t-statistic, the approximate p-value, and the 95% confidence interval. Use the worked solution guide after your attempt so the p-value verdict and CI interpretation are explicit.",
  introAlert: 'Report both a p-value verdict and a 95% confidence interval. If the interval crosses zero, that lines up with "not significant." Open the worked solution to check the difference, SE, t-statistic, p-value verdict, and CI.',
  decisionFramework: {
    title: 'Flagged decision framework: p-values and confidence intervals',
    description: 'A complete finding has two decisions: whether the effect is credible and whether the plausible range is useful enough for action.',
    rules: [
      'p >= 0.05 or CI crosses zero: call the finding inconclusive; recommend more data or a cleaner test.',
      'p < 0.05 and CI does not cross zero: call the effect statistically credible, then use the CI range to size the decision.',
      'Wide CI: act cautiously and forecast from the conservative bound, not the headline difference.',
      'Narrow CI away from zero: stronger evidence for a confident operational recommendation.',
    ],
    responseFrame: 'Frame the response as: "The observed difference is [value]. The p-value verdict is [significant / not significant], and the 95% CI is [lower to upper]. Because the interval [crosses / does not cross] zero, I would [pause / extend / roll out], using [the lower bound / the full range] when explaining risk."',
  },
  lenses: STATS_CLINIC_07_LENSES,
  workingField: null,
  items: STATS_CLINIC_07_ITEMS,
};
